import React from 'react';
import clsx from 'clsx';
import Icon from '@mdi/react';
import { mdiCheck } from '@mdi/js';

type SegmentedSelectItem = {
	id: string;
	text?: string;
	icon?: React.ReactNode;
}

interface SegmentedSelectProps {
	items: SegmentedSelectItem[];
	selected: string[]; // for ids
	onChange: (selected: string[]) => void;
	disabled?: boolean;
	multiple?: boolean;
	/**  Used only when multiple: false, though can be extended to the general case by changing handleClick */
	noEmptySelected?: boolean;
}

function CheckIcon({ disabled = false }: { disabled?: boolean}) {
	return (
		<Icon path={mdiCheck} size="20px" className={disabled ? 'text-fg-primary_disabled' : ''} />
	);
}

export function SegmentedSelect({ items, selected, onChange, disabled = false, multiple = false, noEmptySelected = false }: SegmentedSelectProps) {
	const handleClick = (id: string) => {
		if (disabled) return;
		if (multiple) {
			onChange(selected.includes(id) ? selected.filter((i) => i !== id) : [...selected, id]);
		} else {
			const alreadySelected = selected.includes(id);
			if (noEmptySelected && alreadySelected) return; // prevent returning []
			onChange(alreadySelected ? [] : [id]);
		}
	};
	return (
		<div className="inline-flex items-center rounded-sm border border-solid border-borders-primary">
			{items.map(({ id, text, icon }, index) => {
				const isSelected = selected.includes(id);
				return (
					<button
						key={id}
						onClick={() => handleClick(id)}
						className={clsx(
							'flex px-3 py-2.5 justify-center items-center gap-2',
							isSelected ? 'bg-bg-interactive-light-bg-active_selected' : 'bg-bg-interactive-light-bg-active_default',
							index === 0 && 'rounded-l-sm',
							index === items.length - 1 ? 'rounded-r-sm' : 'border-r-1 border-solid border-borders-primary',
						)}
					>
						{isSelected ? (
							<>
								<CheckIcon disabled={disabled} />
								<span className={clsx(
									'text-sm font-semibold',
									disabled && 'text-fg-primary_disabled',
								)}>{text || icon}</span>
							</>
						) : (
							<>
								{!!icon && <span className={disabled ? 'text-fg-primary_disabled' : ''}>{icon}</span>}
								{!!text && (
									<span className={clsx(
										'text-sm',
										disabled && 'text-fg-primary_disabled',
									)}>
										{text}
									</span>
								)}
							</>
						)}
					</button>
				);
			})}
		</div>
	);
}
